import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trophy, Medal, ShieldCheck } from "lucide-react";

const NLP_ZONES = [
  { min: 80, key: "lb.level.masterTrainer", tone: "teal", range: "80–100" },
  { min: 55, key: "lb.level.trainer", tone: "teal", range: "55–79" },
  { min: 45, key: "lb.level.masterPractitioner", tone: "amber", range: "45–54" },
  { min: 26, key: "lb.level.practitioner", tone: "amber", range: "26–44" },
  { min: 0, key: "lb.level.novice", tone: "rose", range: "0–25" },
];

function nlpLevel(score) {
  return NLP_ZONES.find((z) => score >= z.min) || NLP_ZONES[NLP_ZONES.length - 1];
}

const BADGE_TONE = {
  teal: "bg-teal-50 text-teal-700 border-teal-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
};

function ZoneLegend({ t }) {
  return (
    <div data-testid="knowledge-zones" className="max-w-xl mx-auto mb-8 grid sm:grid-cols-2 gap-1.5">
      <div className="sm:col-span-2 font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">{t("lb.zonesTitle")}</div>
      {NLP_ZONES.map((z) => (
        <div key={z.key} className="flex items-center gap-2 text-xs">
          <span className={`font-mono w-14 text-center rounded-full border px-1.5 py-0.5 ${BADGE_TONE[z.tone]}`}>{z.range}</span>
          <span className="text-slate-600">{t(z.key)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Leaderboards() {
  const { t } = useLanguage();
  const [knowledge, setKnowledge] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/leaderboard/knowledge"), api.get("/leaderboard/trainers")])
      .then(([k, tr]) => { setKnowledge(k.data); setTrainers(tr.data); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="font-mono text-xs uppercase tracking-widest text-teal-700 mb-3">{t("lb.kicker")}</div>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tight text-slate-900">{t("lb.title")}</h1>
        <p className="mt-4 text-slate-600 max-w-xl mx-auto">{t("lb.subtitle")}</p>
      </div>

      <Tabs defaultValue="trainers">
        <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto mb-8">
          <TabsTrigger value="trainers" data-testid="leaderboard-tab-trainers"><ShieldCheck size={15} className="mr-2" /> {t("lb.tab.trainers")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="leaderboard-tab-users"><Trophy size={15} className="mr-2" /> {t("lb.tab.knowledge")}</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <h2 data-testid="knowledge-board-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 text-center max-w-3xl mx-auto mb-4 leading-[1.05]">
            {t("lb.knowledgeTitle")}
          </h2>
          <p data-testid="knowledge-board-note" className="text-center text-sm text-slate-500 max-w-xl mx-auto mb-8">
            {t("lb.rangeNote")}
          </p>
          <ZoneLegend t={t} />
          {loading ? <Loading t={t} /> : knowledge.length === 0 ? (
            <Empty text={t("lb.emptyKnowledge")} link="/test/knowledge" cta={t("lb.emptyKnowledgeCta")} />
          ) : (
            <div className="space-y-2" data-testid="knowledge-leaderboard">
              {knowledge.map((r, i) => {
                const lvl = nlpLevel(r.score);
                return (
                  <Row key={i} rank={r.rank} title={r.display_name} subtitle={new Date(r.date).toLocaleDateString()}
                    value={`${r.score}`} suffix="/ 100" pct={r.percentage} tone={lvl.tone} badge={t(lvl.key)} />
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="trainers">
          <h2 data-testid="trainers-board-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 text-center max-w-3xl mx-auto mb-4 leading-[1.05]">
            {t("lb.trainersTitle")}
          </h2>
          <p data-testid="trainers-board-note" className="text-center text-sm text-slate-500 max-w-xl mx-auto mb-8">
            {t("lb.rangeNote")}
          </p>
          {loading ? <Loading t={t} /> : trainers.length === 0 ? (
            <Empty text={t("lb.emptyTrainers")} link="/checklist/fake-nlp" cta={t("lb.emptyTrainersCta")} />
          ) : (
            <div className="space-y-2" data-testid="trainers-leaderboard">
              {trainers.map((r, i) => (
                <Row key={i} rank={r.rank} title={r.trainer_name} subtitle={`${r.country ? r.country + " · " : ""}${t("lb.ratedBy", { r: r.reviewer })}`}
                  value={`${r.correct_prob}%`} suffix={t("lb.correct")} pct={r.correct_prob}
                  tone={r.correct_prob >= 66 ? "teal" : r.correct_prob >= 40 ? "amber" : "rose"} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ rank, title, subtitle, value, suffix, pct, tone, badge }) {
  const toneMap = {
    teal: "bg-teal-500", amber: "bg-amber-500", rose: "bg-rose-500",
  };
  const badgeTone = {
    teal: "bg-teal-50 text-teal-700 border-teal-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const medal = rank <= 3;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(rank * 0.02, 0.4) }}
      className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
    >
      <div className={`grid place-items-center w-10 h-10 rounded-lg shrink-0 font-serif font-bold ${medal ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-500 border border-slate-100"}`}>
        {medal ? <Medal size={18} /> : rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-slate-900 truncate">{title}</span>
          {badge && (
            <span data-testid="nlp-level-badge" className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${badgeTone[tone] || badgeTone.teal}`}>
              {badge}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 truncate">{subtitle}</div>
        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${toneMap[tone]}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-serif font-bold text-2xl text-slate-900">{value}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{suffix}</div>
      </div>
    </motion.div>
  );
}

function Loading({ t }) {
  return <div className="text-center py-16 text-slate-400 font-mono text-sm">{t("lb.loading")}</div>;
}

function Empty({ text, link, cta }) {
  return (
    <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-2xl">
      <p className="text-slate-500 max-w-sm mx-auto">{text}</p>
      <Link to={link} className="inline-block mt-5 text-teal-700 font-medium hover:underline">{cta} →</Link>
    </div>
  );
}
